class SoleaFlexFinder extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.dialog = this.querySelector('dialog');
    this.content = this.querySelector('[data-content]');
    this.config = JSON.parse(this.querySelector('[data-flex-config]').textContent);
    this.state = { step: 0, mode: 'quiz' };
    document.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-flex-guide-open]') : null;
      if (trigger?.dataset.flexGuideOpen !== this.dataset.blockId || this.dialog.open) return;
      this.trigger = trigger;
      this.previousOverflow = document.documentElement.style.overflow;
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.render();
    }, { signal });
    this.addEventListener('click', (event) => this.handleClick(event), { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const bounds = this.dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
    }, { signal });
    this.dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = this.previousOverflow || '';
      const trigger = this.trigger?.isConnected ? this.trigger : Array.from(document.querySelectorAll('[data-flex-guide-open]')).find((item) => item.dataset.flexGuideOpen === this.dataset.blockId);
      trigger?.focus({ preventScroll: true });
    }, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (this.dialog?.open) document.documentElement.style.overflow = this.previousOverflow || '';
  }

  text(key, values = {}) {
    let value = this.config.copy[key] || '';
    for (const [name, replacement] of Object.entries(values)) value = value.replaceAll(new RegExp('{{\\s*' + name + '\\s*}}', 'g'), String(replacement));
    return value;
  }

  element(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  button(text, action, className = 'solea-flex-finder__choice', data = {}) {
    const button = this.element('button', className, text);
    button.type = 'button';
    button.dataset.action = action;
    Object.assign(button.dataset, data);
    return button;
  }

  image(src, alt) {
    const image = this.element('img');
    const imageUrl = new URL(src);
    imageUrl.searchParams.set('width', '720');
    image.src = imageUrl.href;
    image.srcset = [240, 480, 720].map((width) => { const url = new URL(src); url.searchParams.set('width', String(width)); return url.href + ' ' + width + 'w'; }).join(', ');
    image.sizes = '(max-width: 749px) 80vw, 400px';
    image.alt = alt;
    image.width = 600;
    image.height = 600;
    image.loading = 'lazy';
    return image;
  }

  card(index, action = 'tone') {
    const shade = this.config.shades[index];
    const card = this.button('', action, 'solea-flex-finder__card', { index, a: index, b: index });
    card.setAttribute('aria-label', this.text('choose', { shade: shade.name }));
    card.append(this.image(shade.model, this.text('model_alt', { shade: shade.name })));
    card.append(this.element('strong', '', shade.name));
    card.append(this.element('small', '', shade.description));
    if (!shade.variantId) card.append(this.element('small', '', this.text('not_carried')));
    return card;
  }

  heading(title, hint) {
    const heading = this.element('h3', 'solea-flex-finder__question', title);
    heading.tabIndex = -1;
    this.content.append(heading);
    if (hint) this.content.append(this.element('p', 'solea-flex-finder__hint', hint));
  }

  navigation() {
    const nav = this.element('div', 'solea-flex-finder__nav');
    if (this.state.step > 0 && this.state.mode === 'quiz') nav.append(this.button(this.text('back'), 'back', 'solea-flex-finder__text-button'));
    nav.append(this.button(this.text('restart'), 'restart', 'solea-flex-finder__text-button'));
    if (this.state.mode === 'quiz') nav.append(this.button(this.text('all_shades'), 'guide', 'solea-flex-finder__text-button'));
    this.content.append(nav);
  }

  render() {
    this.content.replaceChildren();
    for (const button of this.querySelectorAll('[data-mode]')) button.setAttribute('aria-pressed', String(button.dataset.mode === this.state.mode));
    if (this.state.mode === 'guide') {
      this.renderGuide();
    } else {
      this.content.append(this.element('p', 'solea-flex-finder__progress', this.state.step < 4 ? this.text('step', { number: this.state.step + 1 }) : this.text('complete')));
      if (this.state.step === 0) {
        this.heading(this.text('choose_tone'), this.text('tone_hint'));
        const grid = this.element('div', 'solea-flex-finder__grid');
        this.config.shades.forEach((_, index) => grid.append(this.card(index)));
        this.content.append(grid);
      } else if (this.state.step === 1) {
        this.heading(this.text('narrow'), this.text('narrow_hint'));
        const first = Math.max(0, this.state.base - 1);
        const last = Math.min(this.config.shades.length - 1, this.state.base + 1);
        const grid = this.element('div', 'solea-flex-finder__grid');
        for (let index = first; index <= last; index++) grid.append(this.card(index, 'refine'));
        this.content.append(grid);
        const choices = this.element('div', 'solea-flex-finder__choices');
        for (let index = first; index < last; index++) choices.append(this.button(this.text('between', { first: this.config.shades[index].name, second: this.config.shades[index + 1].name }), 'refine', undefined, { a: index, b: index + 1 }));
        this.content.append(choices);
      } else if (this.state.step === 2) {
        this.heading(this.text('sun_question'), this.text('sun_hint'));
        const choices = this.element('div', 'solea-flex-finder__choices');
        for (const key of ['burn', 'rare', 'quick', 'unsure']) choices.append(this.button(this.text(key), 'sun', undefined, { answer: key }));
        this.content.append(choices);
      } else if (this.state.step === 3) {
        this.heading(this.text('preference_question'));
        const choices = this.element('div', 'solea-flex-finder__choices');
        for (const key of ['exact', 'warm']) choices.append(this.button(this.text(key), 'preference', undefined, { answer: key }));
        this.content.append(choices);
      } else {
        this.renderResult();
      }
      this.navigation();
    }
    this.dialog.scrollTop = 0;
    this.content.querySelector('h3')?.focus({ preventScroll: true });
  }

  renderResult() {
    const shade = this.config.shades[this.state.choice];
    this.heading(this.text('result'), this.text('result_hint'));
    const layout = this.element('div', 'solea-flex-finder__result');
    layout.append(this.image(shade.model, this.text('model_alt', { shade: shade.name })));
    const details = this.element('div');
    details.append(this.element('h3', '', shade.name));
    details.append(this.element('p', '', shade.description));
    if (this.state.pair) details.append(this.element('p', '', this.text('pair_note', { first: this.config.shades[this.state.pair[0]].name, second: this.config.shades[this.state.pair[1]].name })));
    if (this.state.preference === 'warm') details.append(this.element('p', '', this.text('warm_note')));
    if (['rare', 'quick'].includes(this.state.sun)) details.append(this.element('p', '', this.text('season_note')));
    details.append(this.element('p', '', this.text('activate_note')));
    if (shade.variantId) {
      if (!shade.available) details.append(this.element('p', 'solea-flex-finder__notice', this.text('sold_out')));
      details.append(this.button(this.text(shade.available ? 'select' : 'sold_out_select', { shade: shade.name }), 'select', 'solea-flex-finder__primary', { index: this.state.choice }));
    } else {
      details.append(this.element('p', 'solea-flex-finder__notice', this.text('not_carried_note')));
      details.append(this.contact());
    }
    layout.append(details);
    this.content.append(layout);
  }

  contact() {
    const link = this.element('a', 'solea-flex-finder__primary', this.text('ask'));
    link.href = 'sms:2892791754';
    return link;
  }

  renderGuide() {
    this.heading(this.text('guide_mode'), this.text('guide_hint'));
    const grid = this.element('div', 'solea-flex-finder__grid solea-flex-finder__compare');
    this.config.shades.forEach((shade, index) => {
      const card = this.element('div', 'solea-flex-finder__card');
      card.append(this.image(shade.model, this.text('model_alt', { shade: shade.name })));
      card.append(this.element('strong', '', shade.name));
      card.append(this.element('p', '', shade.description));
      card.append(this.image(shade.swatch, this.text('swatch_alt', { shade: shade.name })));
      if (shade.variantId) card.append(this.button(this.text(shade.available ? 'select' : 'sold_out_select', { shade: shade.name }), 'select', 'solea-flex-finder__primary', { index }));
      else {
        card.append(this.element('p', 'solea-flex-finder__notice', this.text('not_carried')));
        card.append(this.contact());
      }
      grid.append(card);
    });
    this.content.append(grid);
    const comparison = this.element('div', 'solea-flex-finder__comparison');
    comparison.append(this.image(this.config.armImage, this.text('arm_alt')));
    this.content.append(comparison);
    this.navigation();
  }

  handleClick(event) {
    const modeButton = event.target instanceof Element ? event.target.closest('[data-mode]') : null;
    if (modeButton && this.contains(modeButton)) {
      this.state.mode = modeButton.dataset.mode;
      this.render();
      return;
    }
    const button = event.target instanceof Element ? event.target.closest('button[data-action]') : null;
    if (!button || !this.contains(button)) return;
    switch (button.dataset.action) {
      case 'close': this.dialog.close(); return;
      case 'tone':
        this.state = { mode: 'quiz', step: 1, base: Number(button.dataset.index) };
        break;
      case 'refine': {
        const a = Number(button.dataset.a);
        const b = Number(button.dataset.b);
        this.state.choice = a;
        this.state.pair = a !== b ? [a, b] : null;
        this.state.step = 2;
        break;
      }
      case 'sun': this.state.sun = button.dataset.answer; this.state.step = 3; break;
      case 'preference': this.state.preference = button.dataset.answer; this.state.step = 4; break;
      case 'back': this.state.step = Math.max(0, this.state.step - 1); break;
      case 'restart': this.state = { step: 0, mode: 'quiz' }; break;
      case 'guide': this.state.mode = 'guide'; break;
      case 'select': {
        const shade = this.config.shades[Number(button.dataset.index)];
        if (!shade?.variantId) return;
        const picker = Array.from(document.querySelectorAll('variant-picker')).find((element) => element.dataset.blockId === this.dataset.blockId);
        const input = Array.from(picker?.querySelectorAll('input[type="radio"]') || []).find((element) => element.value === shade.name);
        if (!input) return;
        this.dialog.close();
        input.click();
        return;
      }
      default: return;
    }
    this.render();
  }
}
if (!customElements.get('solea-flex-finder')) customElements.define('solea-flex-finder', SoleaFlexFinder);
